import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, formatApiError, photoUrl } from "@/lib/api";
import { CreditsEditor } from "@/components/admin/CreditsEditor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const small = "px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border transition-colors";
const ROLES = [["", "Tutti"], ["hostess", "Hostess"], ["client", "Clienti"]];

const DeleteDialog = ({ user, onClose, onConfirm }) => (
  <AlertDialog open={!!user} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent className="bg-[#121418] border-white/10 rounded-sm" data-testid="admin-user-delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-3xl font-normal">Eliminare “{user?.name}”?</AlertDialogTitle>
        <AlertDialogDescription className="text-neutral-400">
          {user?.role === "hostess" ? "Profilo, foto, chat, candidature e crediti" : "Account, eventi pubblicati, chat, contatti sbloccati e crediti"} verranno eliminati definitivamente. L'operazione non si può annullare.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-none bg-transparent border-white/20" data-testid="admin-user-delete-cancel">Annulla</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="rounded-none bg-red-500 hover:bg-red-600 text-white" data-testid="admin-user-delete-confirm">Elimina</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const AdminUsers = ({ onChange }) => {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [list, setList] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = useCallback(() => api.get("/admin/users", { params: { q, role } }).then((r) => setList(r.data)), [q, role]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 border-b border-white/15 focus-within:border-[#D4AF37] flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#D4AF37]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, azienda" className="bg-transparent flex-1 py-2.5 text-sm focus:outline-none" data-testid="admin-users-search" />
        </div>
        <div className="flex gap-2">
          {ROLES.map(([v, l]) => (
            <button key={v} onClick={() => setRole(v)} data-testid={`admin-users-role-${v || "all"}`}
              className={`${small} ${role === v ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-6 divide-y divide-white/5 border-y border-white/5">
        {list?.length === 0 && <p className="text-neutral-500 p-10 text-center">Nessun utente</p>}
        {(list || []).map((u) => (
          <div key={u.id} className="py-4 px-2 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`admin-user-${u.id}`}>
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-11 h-11 bg-[#181B20] overflow-hidden shrink-0 flex items-center justify-center font-serif text-xl text-neutral-600">
                {u.photo ? <img src={photoUrl(u.photo)} alt="" className="w-full h-full object-cover" /> : u.name[0]}
              </div>
              <div className="min-w-0">
                <p className="text-neutral-100 truncate">
                  {u.role === "hostess" ? <Link to={`/hostess/${u.id}`} className="hover:text-[#D4AF37]">{u.name}</Link> : u.name}
                  {u.company && <span className="text-neutral-500"> · {u.company}</span>}
                </p>
                <p className="text-[11px] font-mono text-neutral-500 truncate">{u.email} · {u.role === "hostess" ? `Hostess${u.city ? ` · ${u.city}` : ""}` : "Cliente"} · {u.credits} crediti</p>
              </div>
              <div className="flex gap-1.5">
                {u.hidden && <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/30 px-2 py-0.5" data-testid={`admin-user-hidden-badge-${u.id}`}>Nascosta</span>}
                {u.blocked && <span className="text-[10px] uppercase tracking-widest text-red-400 border border-red-400/30 px-2 py-0.5" data-testid={`admin-user-blocked-badge-${u.id}`}>Sospeso</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CreditsEditor user={u} onDone={() => { load(); onChange(); }} />
              {u.role === "hostess" && (
                <button onClick={() => act(() => api.post(`/admin/users/${u.id}/hide`, { value: !u.hidden }), u.hidden ? "Profilo visibile" : "Profilo nascosto")}
                  className={`${small} border-amber-400/40 text-amber-400 hover:bg-amber-400/10`} data-testid={`admin-user-hide-${u.id}`}>{u.hidden ? "Mostra profilo" : "Nascondi profilo"}</button>
              )}
              <button onClick={() => act(() => api.post(`/admin/users/${u.id}/block`, { value: !u.blocked }), u.blocked ? "Account riattivato" : "Account sospeso")}
                className={`${small} border-red-400/40 text-red-400 hover:bg-red-400/10`} data-testid={`admin-user-block-${u.id}`}>{u.blocked ? "Riattiva" : "Sospendi"}</button>
              <button onClick={() => setToDelete(u)} className={`${small} border-red-500 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white`} data-testid={`admin-user-delete-${u.id}`}>Elimina</button>
            </div>
          </div>
        ))}
      </div>
      <DeleteDialog user={toDelete} onClose={() => setToDelete(null)}
        onConfirm={() => { act(() => api.delete(`/admin/users/${toDelete.id}`), "Profilo eliminato"); setToDelete(null); }} />
    </div>
  );
};
